/**
 * Smart image preloader.
 *
 * Native loading="lazy" only starts downloading images when they're very
 * close to the viewport (Chrome: ~1250-3000px depending on connection).
 * This makes below-the-fold images visibly "pop in" as the user scrolls.
 *
 * This script uses IntersectionObserver with a large rootMargin to
 * proactively upgrade lazy images to eager loading well before they enter
 * the viewport — so images are already downloaded by the time the user
 * reaches them.
 *
 * Strategy:
 *   - rootMargin "2500px 0px" = start loading when image is within ~2.5
 *     viewport heights of the visible area
 *   - Observer is idle-scheduled so it never competes with LCP
 *   - Falls back silently on browsers without IntersectionObserver
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  var ROOT_MARGIN = '2500px 0px';
  var observer;

  function upgradeImage(img) {
    // Setting to 'eager' triggers the browser to download immediately.
    if (img.loading === 'lazy') img.loading = 'eager';
    // Hint lower priority so we don't steal bandwidth from LCP.
    if (!img.fetchPriority || img.fetchPriority === 'auto') {
      img.fetchPriority = 'low';
    }
  }

  function onIntersect(entries, obs) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        upgradeImage(entries[i].target);
        obs.unobserve(entries[i].target);
      }
    }
  }

  function observeImages() {
    if (!observer) {
      observer = new IntersectionObserver(onIntersect, {
        rootMargin: ROOT_MARGIN,
      });
    }
    var images = document.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < images.length; i++) observer.observe(images[i]);
  }

  function schedule(fn) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      setTimeout(fn, 200);
    }
  }

  function start() {
    schedule(observeImages);
    // Re-scan when new sections are injected (theme editor, PageFly, etc.)
    if ('MutationObserver' in window) {
      var rescan;
      var mo = new MutationObserver(function () {
        clearTimeout(rescan);
        rescan = setTimeout(function () {
          schedule(observeImages);
        }, 250);
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
